// Deterministic seeded PRNG utilities shared by game modules.
// Pure: no Math.random, no Date. The same seed always yields the same sequence,
// which is what makes match replay reproducible (see docs/09-game-module-sdk.md).

// FNV-1a 32-bit string hash → numeric seed.
export function seedFromString(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

// mulberry32 PRNG → function returning floats in [0, 1).
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Build a PRNG directly from a string seed.
export function rngFromString(seed: string): () => number {
  return mulberry32(seedFromString(seed));
}

// Fisher–Yates shuffle using the supplied PRNG. Returns a new array.
export function shuffle<T>(items: readonly T[], rng: () => number): T[] {
  const arr = items.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = arr[i];
    arr[i] = arr[j];
    arr[j] = tmp;
  }
  return arr;
}

// Inclusive integer range [start, end].
export function range(start: number, end: number): number[] {
  const out: number[] = [];
  for (let n = start; n <= end; n++) out.push(n);
  return out;
}
